import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import {
  createManagementBuildingCostHistorySchema,
  ManagementBuildingCostHistoryFormData,
} from "@/validations/management/edit";

import { useEditMonthlyCashflowResult } from "../../../hooks/useEditMonthlyCashflowResult";

import { getManagementBuildingCostTypeKey, type ManagementBuildingCostType } from "./types";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

// 履歴編集用コンポーネント
export const ManagementBuildingCostHistoryEditor: React.FC<{
  monthlyCashflowResult?: MonthlyCashflowResult;
  costType: ManagementBuildingCostType;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
  propertyBoughtAt: string;
}> = ({ monthlyCashflowResult, costType, onCancel, onSave, onCreate, propertyBoughtAt }) => {
  const classes = useStyles();

  // 動的なスキーマを作成（購入月チェック付き）
  const dynamicSchema = createManagementBuildingCostHistorySchema(propertyBoughtAt);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ManagementBuildingCostHistoryFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      month: dayjs(monthlyCashflowResult?.month).format("YYYY-MM") || "",
      property_tax: monthlyCashflowResult?.property_tax?.toString() || "",
      supplies_cost: monthlyCashflowResult?.supplies_cost?.toString() || "",
      large_scale_repair_fund_cost: monthlyCashflowResult?.large_scale_repair_fund_cost?.toString() || "",
      misc_onetime_cost: monthlyCashflowResult?.misc_onetime_cost?.toString() || "",
    },
  });

  const { editMonthlyCashflowResult } = useEditMonthlyCashflowResult();

  const onSubmit = async (data: ManagementBuildingCostHistoryFormData) => {
    const originalMonth = monthlyCashflowResult?.month;
    const newMonth = `${data.month}-01`;

    const costTypeKey = getManagementBuildingCostTypeKey(costType);
    const fieldsToDelete = [costTypeKey];

    const sendData = {
      ...data,
      month: newMonth,
      ...(data.property_tax && {
        property_tax: parseFormattedNumber(data.property_tax),
      }),
      ...(data.supplies_cost && {
        supplies_cost: parseFormattedNumber(data.supplies_cost),
      }),
      ...(data.large_scale_repair_fund_cost && {
        large_scale_repair_fund_cost: parseFormattedNumber(data.large_scale_repair_fund_cost),
      }),
      ...(data.misc_onetime_cost && {
        misc_onetime_cost: parseFormattedNumber(data.misc_onetime_cost),
      }),
    };

    await editMonthlyCashflowResult(fieldsToDelete, sendData, originalMonth);

    if (onSave) {
      onSave();
    } else if (onCreate) {
      onCreate();
    }
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={2} data-edit-cost-inputs>
          <Controller
            name="month"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="いつから"
                required
                type="month"
                error={!!errors.month}
                errorText={errors.month?.message}
              />
            )}
          />
          {costType === "固定資産税・都市計画税" && (
            <Controller
              name="property_tax"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.property_tax}
                  errorText={errors.property_tax?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "消耗品費" && (
            <Controller
              name="supplies_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.supplies_cost}
                  errorText={errors.supplies_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "設備修繕、大規模修繕費用" && (
            <Controller
              name="large_scale_repair_fund_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.large_scale_repair_fund_cost}
                  errorText={errors.large_scale_repair_fund_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
          {costType === "その他一過性の支出" && (
            <Controller
              name="misc_onetime_cost"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label={monthlyCashflowResult ? "金額" : "新しい金額"}
                  required
                  error={!!errors.misc_onetime_cost}
                  errorText={errors.misc_onetime_cost?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          )}
        </Box>

        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" size="small" fullWidth>
              {monthlyCashflowResult ? "保存する" : "この内容で記録する"}
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
